/**
 * The case's rules as the *Rules* tab lists them, and a draft from its form as the API takes it
 * (ADR-0100). Pure, so the page and the stories map fixtures the same way.
 *
 * The first row is always the list *Watch* fills, even before anything is watched, so the page
 * says where *Mentioned in a new source* comes from. Then any older rule on that list, then every
 * rule on a list of its own, each under its list's name.
 */
import type { EntityOption } from '@/components/entity-picker/EntityPicker'
import type { RuleDraft } from '@/components/monitor/RuleForm'
import type { RuleOrigin, RuleRow } from '@/components/monitor/RulesList'
import type {
  NamedRuleWrite,
  WatchItemDto,
  WatchlistDto,
  WatchRuleDto,
} from '@/services/api/case-monitor'
import { ruleSeverity } from './alert-findings'
import { listName, ruleKind } from './monitor-rows'

const folded = (phrase: string) => phrase.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase()

/** Each spelling once (`Skrýša` and `skrysa` are one phrase), in the order given. */
function oncePerSpelling(phrases: readonly string[]): string[] {
  const seen = new Set<string>()
  return phrases.filter(phrase => {
    const key = folded(phrase)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function entitiesOf(items: readonly WatchItemDto[]): EntityOption[] {
  return items.flatMap(item =>
    item.entity_uid
      ? [{ uid: item.entity_uid, name: item.label ?? item.entity_uid, label: item.entity_type }]
      : []
  )
}

/** A phrase rule's words: its own and its list's, which it searches too. */
function phrasesOf(rule: WatchRuleDto, list: WatchlistDto): string[] {
  const own = Array.isArray(rule.config.phrases)
    ? rule.config.phrases.filter((phrase): phrase is string => typeof phrase === 'string')
    : []
  const listed = list.items.flatMap(item => (item.phrase ? [item.phrase] : []))
  return oncePerSpelling([...own, ...listed])
}

function ruleRow(rule: WatchRuleDto, list: WatchlistDto, origin: RuleOrigin): RuleRow {
  const kind = ruleKind(rule.kind)
  const allWatched = rule.config.all_watched === true
  return {
    id: rule.id,
    name: listName(list),
    kind,
    severity: ruleSeverity(rule.severity),
    phrases: kind === 'phrase' ? phrasesOf(rule, list) : [],
    // An older rule on the default list watches what the watched row shows.
    entities: kind === 'phrase' || allWatched || origin === 'default' ? [] : entitiesOf(list.items),
    allWatched,
    origin,
    editable: origin === 'named' && list.rules.length === 1,
  }
}

function watchedRows(list: WatchlistDto | undefined): RuleRow[] {
  const occurrence = list?.rules.find(rule => rule.kind === 'occurrence')
  const watched: RuleRow = {
    id: occurrence?.id ?? null,
    name: null,
    kind: 'occurrence',
    severity: occurrence ? ruleSeverity(occurrence.severity) : null,
    phrases: [],
    entities: entitiesOf(list?.items ?? []),
    allWatched: false,
    origin: 'watched',
    editable: false,
  }
  const older = (list?.rules ?? []).filter(rule => rule !== occurrence)
  return [watched, ...older.map(rule => ruleRow(rule, list as WatchlistDto, 'default'))]
}

/** The watched row, the default list's older rules, then every rule on a list of its own. */
export function toRuleRows(watchlists: readonly WatchlistDto[]): RuleRow[] {
  const named = watchlists.filter(list => !list.is_default)
  return [
    ...watchedRows(watchlists.find(list => list.is_default)),
    ...named.flatMap(list => list.rules.map(rule => ruleRow(rule, list, 'named'))),
  ]
}

/** Phrases typed into the form: split on commas, semicolons and new lines; each spelling once. */
export function parsePhrases(typed: string): string[] {
  return oncePerSpelling(
    typed
      .split(/[,;\n]/)
      .map(phrase => phrase.trim().replace(/\s+/g, ' '))
      .filter(phrase => phrase !== '')
  )
}

/** The form's draft as `POST` and `PUT /monitor/rules` take it. */
export function toNamedRuleWrite(draft: RuleDraft): NamedRuleWrite {
  const phrase = draft.kind === 'phrase'
  const chosen = !phrase && !draft.allWatched
  return {
    name: draft.name.trim(),
    kind: draft.kind,
    severity: draft.severity,
    phrases: phrase ? parsePhrases(draft.phrasesText) : [],
    entities: chosen
      ? draft.entities.map(entity => ({
          uid: entity.uid,
          label: entity.name,
          entity_type: entity.label,
        }))
      : [],
    all_watched: !phrase && draft.allWatched,
  }
}
